import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Icon,
  Input,
  SimpleGrid,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { messages } from "consts";
import { validationErrorHandler } from "lib";
import { useApiGet } from "hooks";
import * as yup from "yup";
import { MdSave, MdChevronLeft } from "react-icons/md";
import api from "lib/api";
import { Emojis } from "components";

export const AttendanceQuickMessagesDetails = () => {
  const { _id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [data, loadingData, refreshData] = useApiGet(`/attendance-quick-messages/${_id}`);
  const [loadingSaveData, setLoadingSaveData] = useState(false);
  const inputRef = useRef();
  const toast = useToast();

  useEffect(() => {
    setFormData(data ?? { active: true });
  }, [data]);

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      const schema = yup.object().shape({
        body: yup.string().required(messages.error.required),
      });
      await schema.validate(formData);
      handleSaveData(formData);
      setFormErrors({});
    } catch (error) {
      setFormErrors({ [error.path]: error.errors });
    }
  };

  const handleSaveData = async (data) => {
    try {
      setLoadingSaveData(true);
      if (_id) await api.put(`/attendance-quick-messages/${_id}`, data);
      else {
        const saved = await api.post("/attendance-quick-messages", data);
        navigate(`/attendance-quick-messages/details/${saved._id}`, { replace: true });
      }
      refreshData();
      toast({ description: messages.success.saveData, status: "success", isClosable: true });
    } catch (error) {
      validationErrorHandler(error, setFormErrors);
      toast({ description: error.message, status: "error", isClosable: true });
    } finally {
      setLoadingSaveData(false);
    }
  };

  return (
    <Box>
      <HStack marginBottom="20px" justify="space-between">
        <Breadcrumb fontWeight="medium" fontSize="sm">
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/home">
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/attendance-quick-messages">
              Assuntos de atendimento
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>{loadingData ? <Spinner size="xs" /> : data?.body ?? "Novo"}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
      </HStack>

      <form onSubmit={handleSubmit}>
        <SimpleGrid spacing={4} mb={4}>
          <Emojis
            isOpen={true}
            inputRef={inputRef}
            value={formData.body}
            onSubmit={(body) => setFormData((state) => ({ ...state, body }))}
          />
          <FormControl isRequired={true} isInvalid={formErrors.body}>
            <Input
              ref={inputRef}
              placeholder="Digite uma mensagem"
              variant="filled"
              value={formData.body ?? ""}
              onChange={({ target }) => setFormData((state) => ({ ...state, body: target.value }))}
            />
            <FormErrorMessage>{formErrors.body}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>

        <HStack justify="flex-end" spacing={4} mt={6}>
          <Button as={RouterLink} to="/attendance-quick-messages" leftIcon={<Icon as={MdChevronLeft} />}>
            Voltar
          </Button>
          <Button type="submit" leftIcon={<Icon as={MdSave} />} colorScheme="main" isLoading={loadingSaveData || loadingData}>
            Salvar
          </Button>
        </HStack>
      </form>
    </Box>
  );
};
